import client from "services/ngage/api-client";
import {Spotlight, SpotlightData} from "types/Spotlight";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";
import deserializeSpotlight from "./deserializers/spotlight";

type SpotlightFilters = {
    search?: string | null
    enabled?: boolean
    page?: number
    perPage?: number
}

/**
 * Get all spotlights that match the given filters (paginated)
 */
const filtered = async (filters: SpotlightFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<Spotlight> | null>('/spotlights', {params})).data

        if (page) page.data = page.data.map(deserializeSpotlight)

        return page
    } catch (error) {
        console.error("Unable to fetch spotlights", error)
        return null
    }
}

/**
 * Get a single spotlight
 *
 * @param {number} spotlightId
 */
const get = async (spotlightId: number) => {
    try {
        const spotlight = (await client.get<Spotlight | null>(`/spotlights/${spotlightId}`)).data
        return spotlight ? deserializeSpotlight(spotlight) : null
    } catch (error) {
        console.error(`Unable to fetch spotlight ${spotlightId}`, error)
        return null
    }
}

/**
 * Get the current spotlight
 */
const current = async () => {
    try {
        const spotlight = (await client.get<Spotlight | null>('/spotlights/current')).data
        return spotlight ? deserializeSpotlight(spotlight) : null
    } catch (error) {
        console.error('Unable to fetch current spotlight', error)
        return null
    }
}

/**
 * Store a new spotlight
 *
 * @param data
 */
const store = async (data: SpotlightData) => {
    try {
        const spotlight = (await client.post<Spotlight | null>('/spotlights', data)).data
        return spotlight ? deserializeSpotlight(spotlight) : null
    } catch (error) {
        console.error("Unable to store spotlight", error)
        throw error
    }
}

/**
 * Update an existing spotlight
 *
 * @param {number} spotlightId
 * @param data
 */
const update = async (spotlightId: number, data: SpotlightData) => {
    try {
        const spotlight = (await client.put<Spotlight | null>(`/spotlights/${spotlightId}`, data)).data
        return spotlight ? deserializeSpotlight(spotlight) : null
    } catch (error) {
        console.error(`Unable to update spotlight ${spotlightId}`, error)
        throw error
    }
}

/**
 * Permanently delete a spotlight
 *
 * @param {number} spotlightId
 */
const destroy = async (spotlightId: number) => {
    try {
        return (await client.delete<null>(`/spotlights/${spotlightId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete spotlight ${spotlightId}`, error)
        throw error
    }
}

const spotlights = {
    filtered,
    get,
    current,
    store,
    update,
    destroy
}

export default spotlights;